import { Badge, Box, Card, Flex, HStack, Icon, Stack, Text } from '@chakra-ui/react';
import { FC } from 'react';
import { FiChevronRight, FiFileText } from 'react-icons/fi';
import { FileEntry } from '../types';

type FileSectionProps = {
  files: FileEntry[];
  onOpenFile: (path: string) => void;
  formatTimestamp: (value?: string | null) => string;
};

const FileSection: FC<FileSectionProps> = ({ files, onOpenFile, formatTimestamp }) => {
  if (files.length === 0) {
    return (
      <Stack gap="3">
        <HStack justify="space-between">
          <Text fontWeight="medium" textTransform="uppercase" fontSize="xs" color="fg.muted">
            Files
          </Text>
          <Text fontSize="xs" color="fg.muted">
            0 total
          </Text>
        </HStack>
        <Text color="fg.muted" fontSize="sm">
          No files in this directory.
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="3">
      <HStack justify="space-between">
        <Text fontWeight="medium" textTransform="uppercase" fontSize="xs" color="fg.muted">
          Files
        </Text>
        <Text fontSize="xs" color="fg.muted">
          {files.length} total
        </Text>
      </HStack>
      <Card.Root variant="outline" borderRadius="xl" overflow="hidden">
        <Stack gap="0">
          {files.map((file, index) => (
            <Flex
              key={file.id}
              align="center"
              px="4"
              py="3"
              gap="4"
              _hover={{ bg: 'bg.muted' }}
              cursor="pointer"
              onClick={() => onOpenFile(file.path)}
              borderTopWidth={index === 0 ? 0 : '1px'}
              borderColor="border.subtle"
            >
              <HStack gap="3" flex="1" minW={0}>
                <Box
                  bg="bg.subtle"
                  borderRadius="md"
                  boxSize="8"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon as={FiFileText} boxSize="4" color="fg.muted" />
                </Box>
                <Stack gap="0" minW={0}>
                  <Text fontWeight="medium" truncate>
                    {file.name}
                  </Text>
                  <Text fontSize="xs" color="fg.muted" truncate>
                    {file.mimeType ?? 'Unknown type'}
                  </Text>
                </Stack>
              </HStack>
              <Badge variant="subtle" colorPalette="gray">
                v{file.latestVersion ?? 1}
              </Badge>
              <Text fontSize="sm" color="fg.muted" minW="150px" textAlign="right">
                {formatTimestamp(file.updatedAt)}
              </Text>
              <Icon as={FiChevronRight} color="fg.muted" />
            </Flex>
          ))}
        </Stack>
      </Card.Root>
    </Stack>
  );
};

export default FileSection;
